// This file is auto-generated by @hey-api/openapi-ts

import { type InfiniteData, infiniteQueryOptions, queryOptions, type UseMutationOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { client } from '../client.gen';
import { bulkUpdateFavorite, bulkUpdateTags, bulkUpdateTrash, createBookmark, createBookmarkByUrl, createTag, deleteItems, deleteTag, fetchItemsTagsStats, getBookmark, getTags, type Options, searchItems, searchItemsAutocomplete, sendEmail, updateBookmark, updateTag } from '../sdk.gen';
import type { BulkUpdateFavoriteData, BulkUpdateFavoriteError, BulkUpdateFavoriteResponse, BulkUpdateTagsData, BulkUpdateTagsError, BulkUpdateTagsResponse, BulkUpdateTrashData, BulkUpdateTrashError, BulkUpdateTrashResponse, CreateBookmarkByUrlData, CreateBookmarkByUrlError, CreateBookmarkByUrlResponse, CreateBookmarkData, CreateBookmarkError, CreateBookmarkResponse, CreateTagData, CreateTagError, CreateTagResponse, DeleteItemsData, DeleteItemsError, DeleteItemsResponse, DeleteTagData, DeleteTagError, DeleteTagResponse, FetchItemsTagsStatsData, FetchItemsTagsStatsError, FetchItemsTagsStatsResponse, GetBookmarkData, GetTagsData, SearchItemsAutocompleteData, SearchItemsData, SearchItemsError, SearchItemsResponse, SendEmailData, SendEmailError, SendEmailResponse, UpdateBookmarkData, UpdateBookmarkError, UpdateBookmarkResponse, UpdateTagData, UpdateTagError, UpdateTagResponse } from '../types.gen';

export type QueryKey<TOptions extends Options> = [
    Pick<TOptions, 'baseURL' | 'body' | 'headers' | 'path' | 'query'> & {
        _id: string;
        _infinite?: boolean;
        tags?: ReadonlyArray<string>;
    }
];

const createQueryKey = <TOptions extends Options>(id: string, options?: TOptions, infinite?: boolean, tags?: ReadonlyArray<string>): [
    QueryKey<TOptions>[0]
] => {
    const params: QueryKey<TOptions>[0] = { _id: id, baseURL: options?.baseURL || (options?.client ?? client).getConfig().baseURL } as QueryKey<TOptions>[0];
    if (infinite) {
        params._infinite = infinite;
    }
    if (tags) {
        params.tags = tags;
    }
    if (options?.body) {
        params.body = options.body;
    }
    if (options?.headers) {
        params.headers = options.headers;
    }
    if (options?.path) {
        params.path = options.path;
    }
    if (options?.query) {
        params.query = options.query;
    }
    return [
        params
    ];
};

export const getBookmarkQueryKey = (options: Options<GetBookmarkData>) => createQueryKey('getBookmark', options);

/**
 * Returns a bookmark item
 */
export const getBookmarkOptions = (options: Options<GetBookmarkData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await getBookmark({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: getBookmarkQueryKey(options)
    });
};

/**
 * Updates a bookmark item and returns it
 */
export const updateBookmarkMutation = (options?: Partial<Options<UpdateBookmarkData>>): UseMutationOptions<UpdateBookmarkResponse, AxiosError<UpdateBookmarkError>, Options<UpdateBookmarkData>> => {
    const mutationOptions: UseMutationOptions<UpdateBookmarkResponse, AxiosError<UpdateBookmarkError>, Options<UpdateBookmarkData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await updateBookmark({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Creates a bookmark item by Url and returns it
 */
export const createBookmarkByUrlMutation = (options?: Partial<Options<CreateBookmarkByUrlData>>): UseMutationOptions<CreateBookmarkByUrlResponse, AxiosError<CreateBookmarkByUrlError>, Options<CreateBookmarkByUrlData>> => {
    const mutationOptions: UseMutationOptions<CreateBookmarkByUrlResponse, AxiosError<CreateBookmarkByUrlError>, Options<CreateBookmarkByUrlData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await createBookmarkByUrl({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Creates a bookmark item and returns it
 */
export const createBookmarkMutation = (options?: Partial<Options<CreateBookmarkData>>): UseMutationOptions<CreateBookmarkResponse, AxiosError<CreateBookmarkError>, Options<CreateBookmarkData>> => {
    const mutationOptions: UseMutationOptions<CreateBookmarkResponse, AxiosError<CreateBookmarkError>, Options<CreateBookmarkData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await createBookmark({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Sends an email from template
 */
export const sendEmailMutation = (options?: Partial<Options<SendEmailData>>): UseMutationOptions<SendEmailResponse, AxiosError<SendEmailError>, Options<SendEmailData>> => {
    const mutationOptions: UseMutationOptions<SendEmailResponse, AxiosError<SendEmailError>, Options<SendEmailData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await sendEmail({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Bulk favorite or unfavorite items
 */
export const bulkUpdateFavoriteMutation = (options?: Partial<Options<BulkUpdateFavoriteData>>): UseMutationOptions<BulkUpdateFavoriteResponse, AxiosError<BulkUpdateFavoriteError>, Options<BulkUpdateFavoriteData>> => {
    const mutationOptions: UseMutationOptions<BulkUpdateFavoriteResponse, AxiosError<BulkUpdateFavoriteError>, Options<BulkUpdateFavoriteData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await bulkUpdateFavorite({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Bulk add or remove tags from items
 */
export const bulkUpdateTagsMutation = (options?: Partial<Options<BulkUpdateTagsData>>): UseMutationOptions<BulkUpdateTagsResponse, AxiosError<BulkUpdateTagsError>, Options<BulkUpdateTagsData>> => {
    const mutationOptions: UseMutationOptions<BulkUpdateTagsResponse, AxiosError<BulkUpdateTagsError>, Options<BulkUpdateTagsData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await bulkUpdateTags({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Bulk trash or restore items
 */
export const bulkUpdateTrashMutation = (options?: Partial<Options<BulkUpdateTrashData>>): UseMutationOptions<BulkUpdateTrashResponse, AxiosError<BulkUpdateTrashError>, Options<BulkUpdateTrashData>> => {
    const mutationOptions: UseMutationOptions<BulkUpdateTrashResponse, AxiosError<BulkUpdateTrashError>, Options<BulkUpdateTrashData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await bulkUpdateTrash({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Deletes items (and related resources) in bulk
 */
export const deleteItemsMutation = (options?: Partial<Options<DeleteItemsData>>): UseMutationOptions<DeleteItemsResponse, AxiosError<DeleteItemsError>, Options<DeleteItemsData>> => {
    const mutationOptions: UseMutationOptions<DeleteItemsResponse, AxiosError<DeleteItemsError>, Options<DeleteItemsData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await deleteItems({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Returns a list of tags statistics for items
 */
export const fetchItemsTagsStatsMutation = (options?: Partial<Options<FetchItemsTagsStatsData>>): UseMutationOptions<FetchItemsTagsStatsResponse, AxiosError<FetchItemsTagsStatsError>, Options<FetchItemsTagsStatsData>> => {
    const mutationOptions: UseMutationOptions<FetchItemsTagsStatsResponse, AxiosError<FetchItemsTagsStatsError>, Options<FetchItemsTagsStatsData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await fetchItemsTagsStats({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const searchItemsAutocompleteQueryKey = (options?: Options<SearchItemsAutocompleteData>) => createQueryKey('searchItemsAutocomplete', options);

/**
 * Returns the top 5 relevant items
 */
export const searchItemsAutocompleteOptions = (options?: Options<SearchItemsAutocompleteData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await searchItemsAutocomplete({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: searchItemsAutocompleteQueryKey(options)
    });
};

export const searchItemsQueryKey = (options?: Options<SearchItemsData>) => createQueryKey('searchItems', options);

/**
 * Returns paginated search results for items
 */
export const searchItemsOptions = (options?: Options<SearchItemsData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await searchItems({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: searchItemsQueryKey(options)
    });
};

const createInfiniteParams = <K extends Pick<QueryKey<Options>[0], 'body' | 'headers' | 'path' | 'query'>>(queryKey: QueryKey<Options>, page: K) => {
    const params = {
        ...queryKey[0]
    };
    if (page.body) {
        params.body = {
            ...queryKey[0].body as any,
            ...page.body as any
        };
    }
    if (page.headers) {
        params.headers = {
            ...queryKey[0].headers,
            ...page.headers
        };
    }
    if (page.path) {
        params.path = {
            ...queryKey[0].path as any,
            ...page.path as any
        };
    }
    if (page.query) {
        params.query = {
            ...queryKey[0].query as any,
            ...page.query as any
        };
    }
    return params as unknown as typeof page;
};

export const searchItemsInfiniteQueryKey = (options?: Options<SearchItemsData>): QueryKey<Options<SearchItemsData>> => createQueryKey('searchItems', options, true);

/**
 * Returns paginated search results for items
 */
export const searchItemsInfiniteOptions = (options?: Options<SearchItemsData>) => {
    return infiniteQueryOptions<SearchItemsResponse, AxiosError<SearchItemsError>, InfiniteData<SearchItemsResponse>, QueryKey<Options<SearchItemsData>>, number | Pick<QueryKey<Options<SearchItemsData>>[0], 'body' | 'headers' | 'path' | 'query'>>(
    // @ts-ignore
    {
        queryFn: async ({ pageParam, queryKey, signal }) => {
            // @ts-ignore
            const page: Pick<QueryKey<Options<SearchItemsData>>[0], 'body' | 'headers' | 'path' | 'query'> = typeof pageParam === 'object' ? pageParam : {
                query: {
                    page: pageParam
                }
            };
            const params = createInfiniteParams(queryKey, page);
            const { data } = await searchItems({
                ...options,
                ...params,
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: searchItemsInfiniteQueryKey(options)
    });
};

/**
 * Deletes tag
 */
export const deleteTagMutation = (options?: Partial<Options<DeleteTagData>>): UseMutationOptions<DeleteTagResponse, AxiosError<DeleteTagError>, Options<DeleteTagData>> => {
    const mutationOptions: UseMutationOptions<DeleteTagResponse, AxiosError<DeleteTagError>, Options<DeleteTagData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await deleteTag({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Updates a tag and returns it
 */
export const updateTagMutation = (options?: Partial<Options<UpdateTagData>>): UseMutationOptions<UpdateTagResponse, AxiosError<UpdateTagError>, Options<UpdateTagData>> => {
    const mutationOptions: UseMutationOptions<UpdateTagResponse, AxiosError<UpdateTagError>, Options<UpdateTagData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await updateTag({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const getTagsQueryKey = (options?: Options<GetTagsData>) => createQueryKey('getTags', options);

/**
 * Returns all tags
 */
export const getTagsOptions = (options?: Options<GetTagsData>) => {
    return queryOptions({
        queryFn: async ({ queryKey, signal }) => {
            const { data } = await getTags({
                ...options,
                ...queryKey[0],
                signal,
                throwOnError: true
            });
            return data;
        },
        queryKey: getTagsQueryKey(options)
    });
};

/**
 * Creates a tag and returns it
 */
export const createTagMutation = (options?: Partial<Options<CreateTagData>>): UseMutationOptions<CreateTagResponse, AxiosError<CreateTagError>, Options<CreateTagData>> => {
    const mutationOptions: UseMutationOptions<CreateTagResponse, AxiosError<CreateTagError>, Options<CreateTagData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await createTag({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};
